import { Center, Loader, rem } from '@mantine/core';
import { refusalText } from '@reditelji/shared';
import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router';
import { useSession } from '../api/session';
import { DailyDigestCard } from '../components/DailyDigestCard';
import { useDeepLinks } from '../lib/deepLinks';
import { useNow } from '../live/clock';
import { useOverview, type OverviewPayload } from '../api/overview';
import type { ChangelogEntry } from '../api/types';
import {
  ago,
  plDni,
  covTone,
  dayPhase,
  dayToneAt,
  dur,
  ini,
  m2t,
  phaseLabel,
  plS,
  sinceLabel,
} from '../lib/ui';
import { useUi } from '../state/ui';
import { CoverageHeat } from './heat/CoverageHeat';

type BlockerKind = 'zavrnitev' | 'opomba' | 'vrzel' | 'konflikt' | 'opomnik' | 'dolgidan';

/** [dot, bg, borderTop] per blocker kind. */
const KIND: Record<BlockerKind, [string, string, string]> = {
  zavrnitev: ['oklch(0.58 0.13 25)', 'oklch(0.99 0.012 25)', 'oklch(0.92 0.03 25)'],
  opomba: ['oklch(0.52 0.14 15)', 'oklch(0.985 0.018 15)', 'oklch(0.9 0.04 15)'],
  vrzel: ['oklch(0.62 0.11 45)', 'oklch(0.995 0.01 45)', 'oklch(0.93 0.025 45)'],
  konflikt: ['oklch(0.55 0.1 285)', 'oklch(0.995 0.008 285)', 'oklch(0.93 0.02 285)'],
  opomnik: ['oklch(0.68 0.11 75)', 'oklch(0.995 0.01 75)', 'oklch(0.94 0.025 75)'],
  dolgidan: ['oklch(0.62 0.05 245)', 'oklch(0.995 0.005 245)', 'oklch(0.94 0.012 245)'],
};

/** Change-tag colours. */
const CT: Record<string, string> = {
  novo: 'oklch(0.55 0.08 155)',
  zamenjava: 'oklch(0.5 0.09 245)',
  premik: 'oklch(0.55 0.1 285)',
  odpadlo: 'oklch(0.58 0.13 25)',
  odstranjeno: 'oklch(0.58 0.13 25)',
  sproščeno: 'oklch(0.58 0.13 25)',
  vrnjeno: 'oklch(0.55 0.08 155)',
  spremenjeno: 'oklch(0.5 0.09 245)',
};

const HEADING: React.CSSProperties = {
  fontSize: rem(12),
  letterSpacing: '0.05em',
  fontWeight: 600,
  color: 'oklch(0.48 0.012 90)',
  whiteSpace: 'nowrap',
};

const CARD: React.CSSProperties = {
  background: 'oklch(1 0 0)',
  border: '1px solid oklch(0.92 0.006 95)',
  borderRadius: 12,
};

interface BlockerRow {
  kind: BlockerKind;
  title: string;
  meta: string;
  act: string;
  onClick: () => void;
}

export function OverviewPage() {
  const overview = useOverview();
  const navigate = useNavigate();
  const { setSelectedDay } = useUi();
  const [filter, setFilter] = useState<'vse' | BlockerKind>('vse');
  // Reminders are send status and recent changes are Dnevnik rows (SPEC §1.1L);
  // the API leaves both empty for a login without them, so their controls go too.
  const { can } = useSession();
  const canComms = can('comms:read');

  const data = overview.data;
  // One Europe/Ljubljana clock for the whole page, so the day cards, the feed
  // and the heatmap agree on which day is today.
  const clock = useNow();
  const today = clock.isoDate;

  const { goShift, goGap, goPerson, goTeam } = useDeepLinks();

  const blockers: BlockerRow[] = useMemo(() => {
    if (!data) return [];
    const dayById = new Map(data.days.map((d) => [d.id, d]));
    const label = (id: string) => dayById.get(id)?.label ?? '';
    const isActive = (id: string) => {
      const d = dayById.get(id);
      return d ? dayPhase(d.date, today) !== 'past' : true;
    };

    const rej: BlockerRow[] = data.blockers.rejections.map((r) => ({
      kind: 'zavrnitev',
      // The shift itself where one is held: "ne more: Kr1 · Sob 14:00–18:00".
      title: `${r.name} — ${r.refused ? refusalText({ ...r.refused, dayLabel: label(r.eventDayId) }) : 'razpored zavrnjen'}`,
      // The words are theirs to read only with comms:read; blank is not "no words".
      meta: canComms
        ? (r.comment || 'brez pojasnila').slice(0, 96) + (r.at ? ` · ${new Date(r.at).toLocaleDateString('sl-SI')}` : '')
        : 'odgovor pri ekipi redarjev',
      act: 'Odpri v urniku',
      onClick: () => goShift(r.eventDayId, r.shiftId),
    }));

    const gaps: BlockerRow[] = data.blockers.gaps
      .filter((g) => isActive(g.eventDayId))
      .map((g) => ({
        kind: 'vrzel',
        title: `${g.postCode} · ${m2t(g.fromMin)}–${m2t(g.toMin)}${g.urgent ? '  · nujno mesto' : ''}`,
        meta: `${label(g.eventDayId)} · ${g.micro || g.sectorName} · nepokrito ${dur(g.toMin - g.fromMin)}`,
        act: 'Poišči kandidata',
        onClick: () => goGap(g),
      }));

    const clashes: BlockerRow[] = data.blockers.conflicts
      .filter((c) => isActive(c.eventDayId))
      .map((c) => ({
        kind: 'konflikt',
        title:
          c.kind === 'overlap'
            ? `${c.name} je hkrati na ${c.postCodes[0]} in ${c.postCodes[1]}`
            : `${c.name}: kratek presledek med ${c.postCodes[0]} in ${c.postCodes[1]}`,
        meta:
          c.kind === 'overlap'
            ? `${label(c.eventDayId)} · ${m2t(c.fromMin)}–${m2t(c.toMin)} prekrivanja`
            : `${label(c.eventDayId)} · samo ${c.gapMin ?? 0} min presledka`,
        act: 'Odpri v urniku',
        onClick: () => goShift(c.eventDayId, c.shiftIds[1]),
      }));

    const rem: BlockerRow[] = data.blockers.reminders.slice(0, 14).map((r) => ({
      kind: 'opomnik',
      title: `${r.name} — razpored še ni potrjen`,
      meta:
        r.briefingSentAt === null
          ? `brifing še ni poslan · ${plS(r.shiftCount)}`
          : `ni potrdil · napotnica ${ago(r.waitingDays ?? 0)} · ${plS(r.shiftCount)}`,
      act: 'Pošlji opomnik',
      onClick: () => goPerson(r.volunteerId, 'sporocila'),
    }));

    const long: BlockerRow[] = data.blockers.longDays
      .filter((l) => isActive(l.eventDayId))
      .map((l) => ({
        kind: 'dolgidan',
        title: `${l.name} ima ${dur(l.totalMin)} na en dan`,
        meta: `${label(l.eventDayId)} · nad 10 h na terenu`,
        act: l.externalGroupId ? 'Odpri ekipo' : 'Odpri profil',
        onClick: () =>
          l.externalGroupId ? goTeam(l.externalGroupId) : goPerson(l.volunteerId),
      }));

    // The Organizacija tab (SPEC §1.1L): a question the team owes, or a reply
    // the organisation has not opened — whichever this viewer is.
    const notes: BlockerRow[] = data.blockers.notes.map((n) => ({
      kind: 'opomba',
      title:
        n.kind === 'question' ? `${n.name} — organizacija čaka odgovor` : `${n.name} — ekipa je odgovorila`,
      meta: sinceLabel(n.since) ?? '',
      act: 'Odpri opombe',
      onClick: () => goPerson(n.volunteerId, 'organizacija'),
    }));

    return [...rej, ...notes, ...gaps, ...clashes, ...rem, ...long];
  }, [data, today, canComms]);

  if (!data) {
    return (
      <Center mih="60vh">
        <Loader />
      </Center>
    );
  }

  const dayById = new Map(data.days.map((d) => [d.id, d]));
  const count = (k: BlockerKind) => blockers.filter((b) => b.kind === k).length;
  const shown = (filter === 'vse' ? blockers : blockers.filter((b) => b.kind === filter)).slice(
    0,
    14,
  );
  const more =
    Math.max(0, (filter === 'vse' ? blockers.length : count(filter)) - shown.length);

  const totGap = data.days
    .filter((d) => dayPhase(d.date, today) !== 'past')
    .reduce((a, d) => a + d.stats.gapMin, 0);

  const changes = data.recentChanges.filter((e) => e.entity === 'shift');

  return (
    <div style={{ flex: 1, overflowY: 'auto', background: 'oklch(0.978 0.004 95)', height: 'calc(100vh - 58px)' }}>
      <div
        style={{
          maxWidth: 1400,
          margin: '0 auto',
          padding: '20px 22px 44px',
          display: 'flex',
          flexDirection: 'column',
          gap: 20,
        }}
      >
        {/* --- Pripravljenost po dnevih --- */}
        <div>
          <div style={{ display: 'flex', alignItems: 'baseline', gap: 10, marginBottom: 10 }}>
            <span style={HEADING}>PRIPRAVLJENOST PO DNEVIH</span>
            <span style={{ fontSize: rem(12.5), color: 'oklch(0.6 0.012 90)' }}>
              {totGap ? `Skupno nepokrito ${dur(totGap)}` : 'Vsa mesta so pokrita'}
            </span>
          </div>
          <div
            style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fit, minmax(258px, 1fr))',
              gap: 12,
            }}
          >
            {data.days.map((d, i) => (
              <DayCard
                key={d.id}
                day={d}
                tone={dayToneAt(i)}
                phase={dayPhase(d.date, today)}
                onClick={() => {
                  setSelectedDay(d.id);
                  void navigate('/casovnica');
                }}
              />
            ))}
          </div>
        </div>

        {/* --- Pokritost po urah --- */}
        <CoverageHeat data={data} clock={clock} />

        {/* --- Dnevni povzetek (AI) --- */}
        <DailyDigestCard />

        {/* --- Kaj me blokira + Ljudje --- */}
        <div
          style={{
            display: 'grid',
            gridTemplateColumns: 'minmax(0, 1.55fr) minmax(0, 1fr)',
            gap: 16,
            alignItems: 'start',
          }}
        >
          <div style={CARD}>
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 10,
                padding: '13px 16px 11px',
                flexWrap: 'wrap',
              }}
            >
              <span style={HEADING}>KAJ ME BLOKIRA</span>
              <span style={{ fontSize: rem(12), color: 'oklch(0.62 0.012 90)' }}>
                {canComms
                  ? 'zavrnitve → organizacija → vrzeli → konflikti → opomniki → dolgi dnevi'
                  : 'zavrnitve → organizacija → vrzeli → konflikti → dolgi dnevi'}
              </span>
              <div style={{ flex: 1 }} />
              <div style={{ display: 'flex', gap: 5, flexWrap: 'wrap' }}>
                {(
                  [
                    ['vse', 'Vse', blockers.length],
                    ['zavrnitev', 'Zavrnitve', count('zavrnitev')],
                    ['opomba', 'Organizacija', count('opomba')],
                    ['vrzel', 'Vrzeli', count('vrzel')],
                    ['konflikt', 'Konflikti', count('konflikt')],
                    ['opomnik', 'Opomniki', count('opomnik')],
                    ['dolgidan', 'Dolgi dnevi', count('dolgidan')],
                  ] as const
                )
                  .filter(([k]) => k !== 'opomnik' || canComms)
                  .map(([k, lbl, n]) => {
                  const on = filter === k;
                  return (
                    <button
                      key={k}
                      onClick={() => setFilter(k)}
                      style={{
                        cursor: 'pointer',
                        font: 'inherit',
                        padding: '4px 9px',
                        borderRadius: 999,
                        fontSize: rem(12),
                        whiteSpace: 'nowrap',
                        background: on ? 'oklch(0.25 0.012 90)' : 'oklch(1 0 0)',
                        color: on ? 'oklch(1 0 0)' : 'oklch(0.45 0.012 90)',
                        border: `1px solid ${on ? 'oklch(0.25 0.012 90)' : 'oklch(0.91 0.006 95)'}`,
                      }}
                    >
                      {lbl} · {n}
                    </button>
                  );
                })}
              </div>
            </div>
            {shown.length ? (
              shown.map((b, i) => {
                const [dot, bg, bd] = KIND[b.kind];
                return (
                  <button
                    key={i}
                    onClick={b.onClick}
                    style={{
                      width: '100%',
                      textAlign: 'left',
                      font: 'inherit',
                      cursor: 'pointer',
                      display: 'flex',
                      alignItems: 'center',
                      gap: 11,
                      padding: '11px 16px',
                      background: bg,
                      border: 0,
                      borderTop: `1px solid ${bd}`,
                    }}
                  >
                    <span
                      style={{
                        width: 7,
                        height: 7,
                        borderRadius: '50%',
                        flexShrink: 0,
                        background: dot,
                      }}
                    />
                    <div
                      style={{
                        display: 'flex',
                        flexDirection: 'column',
                        gap: 2,
                        minWidth: 0,
                        flex: 1,
                      }}
                    >
                      <span
                        style={{
                          fontSize: rem(13.5),
                          fontWeight: 500,
                          color: 'oklch(0.24 0.012 90)',
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          whiteSpace: 'nowrap',
                        }}
                      >
                        {b.title}
                      </span>
                      <span
                        style={{
                          fontSize: rem(12),
                          color: 'oklch(0.58 0.012 90)',
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          whiteSpace: 'nowrap',
                        }}
                      >
                        {b.meta}
                      </span>
                    </div>
                    <span
                      style={{
                        fontSize: rem(12.5),
                        fontWeight: 500,
                        color: 'oklch(0.45 0.08 245)',
                        whiteSpace: 'nowrap',
                      }}
                    >
                      {b.act} →
                    </span>
                  </button>
                );
              })
            ) : (
              <div
                style={{
                  padding: '22px 16px 26px',
                  fontSize: rem(13.5),
                  color: 'oklch(0.55 0.012 90)',
                  borderTop: '1px solid oklch(0.94 0.006 95)',
                }}
              >
                Nič odprtega v tej kategoriji.
              </div>
            )}
            {more > 0 ? (
              <div
                style={{
                  padding: '10px 16px 12px',
                  fontSize: rem(12),
                  color: 'oklch(0.6 0.012 90)',
                  borderTop: '1px solid oklch(0.94 0.006 95)',
                }}
              >
                še {more} vrstic — filtriraj po vrsti zgoraj
              </div>
            ) : null}
          </div>

          <PeopleCard data={data} onOpen={goPerson} showNoResponse={canComms} />
        </div>

        {/* --- Zadnje spremembe --- */}
        {can('audit:read') ? (
          <div
            style={{
              ...CARD,
              padding: '13px 16px 15px',
              display: 'flex',
              flexDirection: 'column',
              gap: 10,
            }}
          >
            <div style={{ display: 'flex', alignItems: 'baseline', gap: 10 }}>
              <span style={HEADING}>ZADNJE SPREMEMBE</span>
              <span style={{ fontSize: rem(12.5), color: 'oklch(0.6 0.012 90)' }}>
                {!data.publication ? null : data.publication.stale || data.publication.never ? (
                  <>
                    {/* What the volunteers can actually see, which is not the
                        same as what the board shows (SPEC §1.1F). */}
                    {data.publication.stale ? (
                      <>
                        neobjavljene spremembe:{' '}
                        <b style={{ color: 'var(--rr-warn-strong)' }}>{data.publication.stale}</b>
                      </>
                    ) : null}
                    {data.publication.stale && data.publication.never ? ' · ' : null}
                    {data.publication.never ? (
                      <>
                        brez objave:{' '}
                        <b style={{ color: 'var(--rr-warn-strong)' }}>{data.publication.never}</b>
                      </>
                    ) : null}
                  </>
                ) : data.renotifyCount ? (
                  `${data.renotifyCount} oseb je zaradi sprememb spet v stanju »čaka«`
                ) : (
                  'Od zadnjega brifinga ni novih sprememb.'
                )}
              </span>
            </div>
            {changes.length ? (
              changes.slice(0, 10).map((e) => (
                <ChangeRow
                  key={e.id}
                  entry={e}
                  dayLabel={e.eventDayId ? (dayById.get(e.eventDayId)?.label ?? '') : ''}
                  onClick={
                    e.entityId && e.kind !== 'deleted' && e.eventDayId
                      ? () => goShift(e.eventDayId!, e.entityId)
                      : undefined
                  }
                />
              ))
            ) : (
              <div style={{ padding: '10px 2px 6px', fontSize: rem(13.5), color: 'oklch(0.55 0.012 90)' }}>
                Razpored je enak kot ob zadnjem poslanem brifingu.
              </div>
            )}
            {changes.length > 10 ? (
              <div style={{ fontSize: rem(11.5), color: 'oklch(0.62 0.012 90)', padding: '4px 2px 0' }}>
                + še {changes.length - 10} sprememb
              </div>
            ) : null}
          </div>
        ) : null}
      </div>
    </div>
  );
}

function DayCard({
  day,
  tone,
  phase,
  onClick,
}: {
  day: OverviewPayload['days'][number];
  tone: ReturnType<typeof dayToneAt>;
  phase: 'past' | 'live' | 'next';
  onClick: () => void;
}) {
  const s = day.stats;
  const bar = covTone(s.pct)[2];
  const phaseStyle = {
    past: { bg: 'oklch(0.965 0.005 95)', fg: 'oklch(0.55 0.012 90)' },
    live: { bg: 'oklch(0.95 0.04 25)', fg: 'oklch(0.48 0.15 25)' },
    next: { bg: 'oklch(0.96 0.02 245)', fg: 'oklch(0.42 0.07 245)' },
  }[phase];
  const risk = s.rejPeople
    ? `${s.rejPeople} zavrnitev še brez rešitve`
    : s.gapCount
      ? `nepokrito ${dur(s.gapMin)} na ${s.gapCount} odsekih`
      : s.pendPeople
        ? `${s.pendPeople} oseb še ni potrdilo`
        : 'brez odprtih zadev';
  const riskFg =
    s.rejPeople || s.gapCount
      ? 'oklch(0.47 0.11 25)'
      : s.pendPeople
        ? 'oklch(0.46 0.09 75)'
        : 'oklch(0.4 0.06 155)';

  return (
    <button
      onClick={onClick}
      style={{
        textAlign: 'left',
        font: 'inherit',
        cursor: 'pointer',
        background: 'oklch(1 0 0)',
        border: '1px solid oklch(0.92 0.006 95)',
        borderLeft: `3px solid ${tone.mid}`,
        borderRadius: 12,
        padding: '15px 16px 15px 14px',
        display: 'flex',
        flexDirection: 'column',
        gap: 12,
      }}
    >
      <div style={{ display: 'flex', alignItems: 'baseline', gap: 8 }}>
        <span
          style={{
            fontSize: rem(14.5),
            fontWeight: 600,
            color: tone.ink,
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
          }}
        >
          {day.label}
        </span>
        <span
          style={{
            fontSize: rem(11),
            fontWeight: 700,
            letterSpacing: '0.04em',
            textTransform: 'uppercase',
            padding: '2px 6px',
            borderRadius: 5,
            background: phaseStyle.bg,
            color: phaseStyle.fg,
          }}
        >
          {phaseLabel[phase]}
        </span>
        <div style={{ flex: 1 }} />
        <span
          style={{
            fontFamily: 'var(--rr-font-mono)',
            fontSize: rem(20),
            fontWeight: 700,
            letterSpacing: '-0.02em',
            color: bar,
          }}
        >
          {s.pct}%
        </span>
      </div>
      <div style={{ height: 6, borderRadius: 3, background: 'oklch(0.93 0.006 95)', overflow: 'hidden' }}>
        <div style={{ height: '100%', width: `${s.pct}%`, background: bar }} />
      </div>
      <div style={{ display: 'flex', gap: 16 }}>
        {(
          [
            [s.nConf, 'potrjenih', 'oklch(0.36 0.07 155)'],
            [s.nPend, 'čaka', 'oklch(0.46 0.09 75)'],
            [s.rejPeople, 'zavrnitev', 'oklch(0.47 0.12 25)'],
            // Counted by the bar above, so it has to be counted here too —
            // otherwise a day mostly covered by teams reads ~100% over figures
            // accounting for a handful of shifts (SPEC §1.1J).
            [s.nExternal, 'zunanjih', 'oklch(0.42 0.012 90)'],
            [s.nOpen, 'nezasedenih', 'oklch(0.42 0.012 90)'],
          ] as const
        )
          .filter(([v, lbl]) => lbl !== 'zunanjih' || v > 0)
          .map(([v, lbl, fg]) => (
          <div key={lbl} style={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
            <span
              style={{ fontFamily: 'var(--rr-font-mono)', fontSize: rem(15), fontWeight: 600, color: fg }}
            >
              {v}
            </span>
            <span style={{ fontSize: rem(11.5), color: 'oklch(0.6 0.012 90)' }}>{lbl}</span>
          </div>
        ))}
      </div>
      <span style={{ fontSize: rem(12.5), color: riskFg }}>{risk}</span>
    </button>
  );
}

function PeopleCard({
  data,
  onOpen,
  showNoResponse,
}: {
  data: OverviewPayload;
  onOpen: (id: string) => void;
  /** Who has answered what, and who wrote last — correspondence. */
  showNoResponse: boolean;
}) {
  const dayById = new Map(data.days.map((d) => [d.id, d]));
  const groups = [
    ...(showNoResponse
      ? [
          {
            title: 'Čaka naš odgovor',
            dot: 'oklch(0.58 0.13 25)',
            cap: 6,
            rows: (data.people.awaitingUs ?? []).map((p) => ({
              id: p.id,
              name: p.name,
              meta: `piše nam · ${ago(p.days)}`,
            })),
          },
          {
            title: 'Brez odgovora',
            dot: 'oklch(0.64 0.12 50)',
            cap: 6,
            rows: (data.people.noReply ?? []).map((p) => ({
              id: p.id,
              name: p.name,
              meta: `na naše sporočilo · ${plDni(p.days)}`,
            })),
          },
          {
            title: 'Ni potrdil',
            dot: 'oklch(0.68 0.11 75)',
            cap: 6,
            rows: (data.people.notConfirmed ?? data.people.noResponse ?? []).map((p) => ({
              id: p.id,
              name: p.name,
              meta: `${dur(p.totalMin)} dodeljeno · razpored še ni potrjen`,
            })),
          },
        ]
      : []),
    {
      title: 'Preobremenjeni',
      dot: 'oklch(0.55 0.1 285)',
      cap: 4,
      rows: data.people.overloaded.map((p) => ({
        id: p.id,
        name: p.name,
        meta: p.days
          .map((d) => `${dayById.get(d.eventDayId)?.label ?? ''} ${dur(d.minutes)}`)
          .join(' · '),
      })),
    },
    {
      title: 'Neizkoriščeni iz nabora',
      dot: 'oklch(0.62 0.09 155)',
      cap: 5,
      rows: data.people.unused.map((p) => ({
        id: p.id,
        name: p.name,
        meta: 'v naboru, brez izmene',
      })),
    },
    {
      // Withdrew from the event (SPEC §1.1K): counted apart, so the others add
      // up to the people still coming. Shown only when there is somebody.
      title: 'Ne pridejo',
      dot: 'oklch(0.55 0.14 25)',
      cap: 5,
      hideEmpty: true,
      rows: data.people.cancelled.map((p) => ({
        id: p.id,
        name: p.name,
        meta: `ne pride · zabeleženo ${new Date(p.cancelledAt).toLocaleDateString('sl-SI', { day: 'numeric', month: 'numeric', timeZone: 'Europe/Ljubljana' })}`,
      })),
    },
  ].filter((g) => !g.hideEmpty || g.rows.length > 0);

  return (
    <div
      style={{
        ...CARD,
        padding: '13px 16px 15px',
        display: 'flex',
        flexDirection: 'column',
        gap: 14,
      }}
    >
      <span style={HEADING}>LJUDJE</span>
      {groups.map((g) => {
        const shown = g.rows.slice(0, g.cap);
        const rest = g.rows.length - shown.length;
        return (
          <div key={g.title} style={{ display: 'flex', flexDirection: 'column', gap: 5 }}>
            <div style={{ display: 'flex', alignItems: 'baseline', gap: 7 }}>
              <span style={{ fontSize: rem(13), fontWeight: 600, color: 'oklch(0.3 0.012 90)' }}>
                {g.title}
              </span>
              <span
                style={{
                  fontFamily: 'var(--rr-font-mono)',
                  fontSize: rem(13),
                  color: 'oklch(0.55 0.012 90)',
                }}
              >
                {g.rows.length}
              </span>
            </div>
            {shown.map((p) => (
              <button
                key={p.id}
                onClick={() => onOpen(p.id)}
                style={{
                  textAlign: 'left',
                  font: 'inherit',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  gap: 9,
                  padding: '6px 8px',
                  borderRadius: 7,
                  border: '1px solid oklch(0.94 0.006 95)',
                  background: 'oklch(0.995 0.002 95)',
                }}
              >
                <span
                  style={{
                    width: 22,
                    height: 22,
                    borderRadius: '50%',
                    flexShrink: 0,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontSize: rem(11),
                    fontWeight: 600,
                    color: 'oklch(1 0 0)',
                    background: g.dot,
                  }}
                >
                  {ini(p.name)}
                </span>
                <div style={{ display: 'flex', flexDirection: 'column', minWidth: 0 }}>
                  <span
                    style={{
                      fontSize: rem(13),
                      color: 'oklch(0.26 0.012 90)',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap',
                    }}
                  >
                    {p.name}
                  </span>
                  <span
                    style={{
                      fontSize: rem(11.5),
                      color: 'oklch(0.6 0.012 90)',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap',
                    }}
                  >
                    {p.meta}
                  </span>
                </div>
              </button>
            ))}
            {rest > 0 ? (
              <span style={{ fontSize: rem(11.5), color: 'oklch(0.62 0.012 90)', paddingLeft: 3 }}>
                + še {rest} — odpri Prostovoljce
              </span>
            ) : null}
          </div>
        );
      })}
    </div>
  );
}

function changeTag(e: ChangelogEntry): string {
  if (e.kind === 'created') return 'novo';
  if (e.kind === 'deleted') return 'odstranjeno';
  // A "Ne pride" and its undo move the holder too, but nobody took over.
  if (e.kind === 'freed') return 'sproščeno';
  if (e.kind === 'restored') return 'vrnjeno';
  const after = (e.after ?? {}) as Record<string, unknown>;
  if (after.state === 'cancelled') return 'odpadlo';
  if ('volunteerId' in after) return 'zamenjava';
  if ('fromMin' in after || 'toMin' in after) return 'premik';
  return 'spremenjeno';
}

function ChangeRow({
  entry,
  dayLabel,
  onClick,
}: {
  entry: ChangelogEntry;
  dayLabel: string;
  onClick?: () => void;
}) {
  const tag = changeTag(entry);
  const after = (entry.after ?? {}) as Record<string, unknown>;
  const before = (entry.before ?? {}) as Record<string, unknown>;
  const range =
    typeof after.fromMin === 'number' && typeof after.toMin === 'number'
      ? `${m2t(after.fromMin)}–${m2t(after.toMin)}`
      : typeof before.fromMin === 'number' && typeof before.toMin === 'number'
        ? `${m2t(before.fromMin)}–${m2t(before.toMin)}`
        : '';
  const text =
    tag === 'premik' &&
    typeof before.fromMin === 'number' &&
    typeof after.fromMin === 'number'
      ? `${entry.entityLabel} · ${m2t(before.fromMin)}–${m2t((before.toMin as number) ?? 0)} → ${m2t(after.fromMin)}–${m2t((after.toMin as number) ?? 0)}`
      : entry.entityLabel;

  return (
    <button
      onClick={onClick}
      disabled={!onClick}
      style={{
        textAlign: 'left',
        font: 'inherit',
        cursor: onClick ? 'pointer' : 'default',
        display: 'flex',
        alignItems: 'center',
        gap: 10,
        padding: '7px 9px',
        borderRadius: 7,
        border: '1px solid oklch(0.95 0.006 95)',
        background: 'oklch(0.995 0.002 95)',
      }}
    >
      <span
        style={{
          fontSize: rem(11),
          letterSpacing: '0.03em',
          fontWeight: 600,
          width: 84,
          flexShrink: 0,
          color: CT[tag] ?? 'oklch(0.5 0.09 245)',
        }}
      >
        {tag}
      </span>
      <span
        style={{
          fontSize: rem(13),
          color: 'oklch(0.26 0.012 90)',
          flex: 1,
          minWidth: 0,
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          whiteSpace: 'nowrap',
        }}
      >
        {text}
      </span>
      <span style={{ fontSize: rem(12), color: 'oklch(0.6 0.012 90)', whiteSpace: 'nowrap' }}>
        {[dayLabel, range].filter(Boolean).join(' · ')}
      </span>
    </button>
  );
}
